
import dayjs from "dayjs";
import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { emptyToLine, splitThousands } from "@/utils/common";
import { Table } from "@/utils/decorator";

const finance = namespace("finance");
const base = namespace("base");

interface SearchForm {
  phone: string;
  accountNo: string;
  accountType: string;
  minAmount: string;
  maxAmount: string;
  customType: string;
}

interface FlowSearchForm {
  id: string;
  billNo: string;
  instructionType: string;
}

interface AccountItem {
  accountNo: string;
  phone: string;
  status: number;
  amount: string;
  [key: string]: any;
}

@Component({})
@Table("loadListData")
export default class VirtualAcctNew extends Vue {
  @finance.Action getVirtualAcctList!: Function;
  @finance.Action getAccountFlowList!: Function;
  @base.Action getDictionaryList!: Function;

  dayjs = dayjs;
  splitThousands = splitThousands;
  emptyToLine = emptyToLine;

  // 搜索表单
  searchForm: SearchForm = {
    phone: "",
    accountNo: "",
    accountType: "",
    minAmount: "",
    maxAmount: "",
    customType: ""
  };

  // 账户列表数据
  virtualAcctList: AccountItem[] = [];

  // 账户类型选项
  accountTypeList: any[] = [
    { label: "全部", value: "" },
    { label: "备付金账户", value: "V0" },
    { label: "余额账户", value: "V1" },
    { label: "提现账户", value: "V2" },
    { label: "收益账户", value: "V3" },
    { label: "手续账户", value: "V4" },
    { label: "待结算账户", value: "V5" },
    { label: "冻结账户", value: "V6" },
    { label: "积分账户", value: "V7" }
  ];

  // 客户类型选项
  customTypeList: any[] = [
    { label: "全部", value: "" },
    { label: "个人", value: "1" },
    { label: "企业", value: "2" },
    { label: "商户", value: "3" },
    { label: "平台", value: "4" }
  ];

  // 表格加载状态
  mix_loading = false;
  mix_sortParams: any[] = [];
  mix_pageNum = 1;
  mix_pageSize = 10;
  mix_total = 0;

  // 高级搜索显示控制
  showAdvancedSearch = false;

  // 动账流水相关
  flowDialogVisible = false;
  currentAccount: any = null;
  accountFlowList: any[] = [];
  flowSearchForm: FlowSearchForm = {
    id: "",
    billNo: "",
    instructionType: ""
  };
  flowLoading = false;
  flowSortParams: any[] = [];
  flowPageNum = 1;
  flowPageSize = 10;
  flowTotal = 0;

  created() {
    this.init();
  }

  init() {
    this.loadListData();
  }

  // 切换高级搜索
  toggleAdvancedSearch() {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  // 搜索处理
  handleSearch() {
    this.mix_pageNum = 1;
    this.loadListData();
  }

  // 重置搜索
  handleReset() {
    this.searchForm = {
      phone: "",
      accountNo: "",
      accountType: "",
      minAmount: "",
      maxAmount: "",
      customType: ""
    };
    this.handleSearch();
  }

  // 加载账户列表数据
  @Table.LoadDataMethod()
  loadListData() {
    this.mix_loading = true;
    const requestParams = {
      orderItems: this.mix_sortParams,
      ...this.searchForm,
      pageSize: this.mix_pageSize,
      pageNum: this.mix_pageNum
    };

    const apiParams = {
      ...requestParams,
      pageSize: Number(this.mix_pageSize),
      pageNum: Number(this.mix_pageNum)
    };

    return this.getVirtualAcctList(apiParams)
      .then((data: any) => {
        this.virtualAcctList = this.$safeValue(data, "data.list", []);
        this.mix_total = this.$safeValue(data, "data.total", 0);
        this.mix_loading = false;
        return data;
      })
      .catch((error) => {
        console.error('获取虚拟账户列表失败:', error);
        this.mix_loading = false;
      });
  }



  // 查看用户详情
  handleUserView(row: AccountItem) {
    let { href } = this.$router.resolve({
      path: `/customer/customer-view/${row.phone}`
    });
    window.open(href, "_blank");
  }

  // 获取账户类型标签类型
  getAccountTypeTag(accountType: string): string {
    const tagMap: { [key: string]: string } = {
      V0: "info",    // 备付金账户
      V1: "primary", // 余额账户
      V2: "success", // 提现账户
      V3: "warning", // 收益账户
      V4: "danger",  // 手续账户
      V5: "",        // 待结算账户
      V6: "info",    // 冻结账户
      V7: "success"  // 积分账户
    };
    return tagMap[accountType] || "primary";
  }

  // 获取账户类型名称
  getAccountType(accountType: string): string {
    const map = {
      V0: "备付金账户",
      V1: "余额账户",
      V2: "提现账户",
      V3: "收益账户",
      V4: "手续账户",
      V5: "待结算账户",
      V6: "冻结账户",
      V7: "积分账户"
    };
    return map[accountType] || "-";
  }

  // 获取客户类型标签类型
  getCustomTypeTag(customType: string): string {
    const tagMap = {
      "1": "primary", // 个人
      "2": "success", // 企业
      "3": "warning", // 商户
      "4": "danger"   // 平台
    };
    return tagMap[customType] || "info";
  }

  // 获取客户类型名称
  getCustomType(customType: string): string {
    const map = {
      "1": "个人",
      "2": "企业",
      "3": "商户",
      "4": "平台"
    };
    return map[customType] || "-";
  }

  // 获取账户状态
  getState(state: number): string {
    const map = {
      1: "启用",
      2: "冻结"
    };
    return map[state] || "-";
  }

  // 查看动账流水
  handleViewAccountFlow(row) {
    this.currentAccount = row;
    this.flowDialogVisible = true;
    this.flowPageNum = 1;
    this.flowSortParams = [];
    this.loadFlowData();
  }

  // 账户类型筛选处理
  handleAccountTypeFilter(accountType) {
    this.searchForm.accountType = accountType;
    this.handleSearch();
  }

  // 流水搜索处理
  handleFlowSearch() {
    this.flowPageNum = 1;
    this.loadFlowData();
  }

  // 客户类型改变处理
  handleCustomTypeChange(value) {
    console.log('客户类型改变:', value);
    // 可以在这里添加其他处理逻辑
  }

  // 交易方向改变处理
  handleInstructionTypeChange(value) {
    console.log('交易方向改变:', value);
    // 可以在这里添加其他处理逻辑
  }

  // 流水重置搜索
  handleFlowReset() {
    this.flowSearchForm = {
      id: "",
      billNo: "",
      instructionType: ""
    };
    this.handleFlowSearch();
  }

  // 加载流水数据
  loadFlowData() {
    if (!this.currentAccount) return;
    this.flowLoading = true;
    this.getAccountFlowList({
      accountNo: this.currentAccount.accountNo,
      orderItems: this.flowSortParams,
      ...this.flowSearchForm,
      pageSize: this.flowPageSize,
      pageNum: this.flowPageNum
    })
      .then(data => {
        this.accountFlowList = this.$safeValue(data, "data.list", []);
        this.flowTotal = this.$safeValue(data, "data.total", 0);
        this.flowLoading = false;
      })
      .catch(() => {
        this.flowLoading = false;
      });
  }

  // 流水分页大小改变
  handleFlowSizeChange(val: number) {
    this.flowPageSize = val;
    this.$forceUpdate(); // 强制更新视图
    this.loadFlowData();
  }

  // 流水页码改变
  handleFlowPageChange(val: number) {
    this.flowPageNum = val;
    this.$forceUpdate(); // 强制更新视图
    this.loadFlowData();
  }
}
